/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const koKR: LocalizationResource = {
  locale: 'ko-KR',
  apiKeys: {
    action__add: '새 키 만들기',
    action__search: '키 검색',
    copySecret: {
      formButtonPrimary__copyAndClose: '복사하고 닫기',
      formHint: '보안상 나중에 다시 볼 수 없어요.',
      formTitle: '지금 "{{name}}" API 키를 복사해 주세요',
    },
    createdAndExpirationStatus__expiresOn:
      "생성 {{ createdDate | shortDate('ko-KR') }} • 만료 {{ expiresDate | longDate('ko-KR') }}",
    createdAndExpirationStatus__never: "생성 {{ createdDate | shortDate('ko-KR') }} • 만료 없음",
    detailsTitle__emptyRow: 'API 키가 없어요',
    formButtonPrimary__add: '키 만들기',
    formFieldCaption__expiration__expiresOn: '{{ date }}에 만료돼요',
    formFieldCaption__expiration__never: '이 키는 만료되지 않아요',
    formFieldOption__expiration__180d: '180일',
    formFieldOption__expiration__1d: '1일',
    formFieldOption__expiration__1y: '1년',
    formFieldOption__expiration__30d: '30일',
    formFieldOption__expiration__60d: '60일',
    formFieldOption__expiration__7d: '7일',
    formFieldOption__expiration__90d: '90일',
    formFieldOption__expiration__never: '만료 없음',
    formHint: '이름을 입력해 새 키를 만들어요. 필요하면 언제든지 폐기할 수 있어요.',
    formTitle: '새 API 키 추가',
    lastUsed__days: '{{days}}일 전',
    lastUsed__hours: '{{hours}}시간 전',
    lastUsed__minutes: '{{minutes}}분 전',
    lastUsed__months: '{{months}}개월 전',
    lastUsed__seconds: '{{seconds}}초 전',
    lastUsed__years: '{{years}}년 전',
    menuAction__revoke: '키 폐기',
    revokeConfirmation: {
      confirmationText: '폐기',
      formButtonPrimary__revoke: '키 폐기',
      formHint: '이 시크릿 키를 삭제할까요?',
      formTitle: '"{{apiKeyName}}" 시크릿 키를 폐기할까요?',
    },
    tableHeader__name: '이름',
    tableHeader__lastUsed: '마지막 사용',
    tableHeader__actions: '동작',
  },
  backButton: '돌아가기',
  badge__activePlan: '활성',
  badge__canceledEndsAt: "취소됨 • {{ date | shortDate('ko-KR') }}에 종료",
  badge__currentPlan: '현재 플랜',
  badge__default: '기본값',
  badge__endsAt: "{{ date | shortDate('ko-KR') }}에 종료",
  badge__expired: '만료됨',
  badge__freeTrial: '무료 체험',
  badge__otherImpersonatorDevice: '기타 사칭 장치',
  badge__pastDueAt: "{{ date | shortDate('ko-KR') }}에 연체",
  badge__pastDuePlan: '연체됨',
  badge__primary: '기본',
  badge__renewsAt: "{{ date | shortDate('ko-KR') }}에 갱신",
  badge__requiresAction: '조치 필요',
  badge__startsAt: "{{ date | shortDate('ko-KR') }}에 시작",
  badge__thisDevice: '이 장치',
  badge__trialEndsAt: "{{ date | shortDate('ko-KR') }}에 체험 종료",
  badge__unverified: '미확인',
  badge__upcomingPlan: '예정된 플랜',
  badge__userDevice: '사용자 장치',
  badge__you: '나',
  billing: {
    addPaymentMethod__label: '결제 수단 추가',
    alwaysFree: '항상 무료',
    annually: '연간',
    availableFeatures: '사용 가능한 기능',
    billedAnnually: '연간 결제',
    billedMonthlyOnly: '월간 결제만 가능',
    cancelFreeTrial: '무료 체험 취소',
    cancelFreeTrialAccessUntil:
      "무료 체험은 {{ date | longDate('ko-KR') }}까지 유지돼요. 이후에는 체험 기능을 사용할 수 없어요. 요금은 청구되지 않아요.",
    cancelFreeTrialTitle: '{{plan}} 플랜 무료 체험을 취소할까요?',
    cancelSubscription: '구독 취소',
    cancelSubscriptionAccessUntil:
      "{{ date | longDate('ko-KR') }}까지 '{{plan}}' 기능을 사용할 수 있어요. 이후에는 이용할 수 없어요.",
    cancelSubscriptionNoCharge: '이 구독에는 요금이 청구되지 않아요.',
    cancelSubscriptionPastDue:
      '구독이 즉시 종료되고 모든 플랜 기능을 사용할 수 없어요. 다음 결제 시 연체 금액을 결제하셔야 해요.',
    cancelSubscriptionTitle: '{{plan}} 구독을 취소할까요?',
    cannotSubscribeMonthly: '이 플랜은 월간 결제가 불가해요. 연간 결제를 선택해 주세요.',
    cannotSubscribeUnrecoverable: '이 플랜으로 구독할 수 없어요. 현재 구독이 더 높은 요금제예요.',
    checkout: {
      description__paymentSuccessful: '결제가 완료됐어요.',
      description__subscriptionSuccessful: '새 구독이 준비됐어요.',
      downgradeNotice: '현재 구독은 결제 주기 종료까지 유지되고, 이후 이 구독으로 전환돼요.',
      emailForm: {
        subtitle: '결제를 완료하려면 영수증을 받을 이메일 주소를 추가해야 해요.',
        title: '이메일 주소 추가',
      },
      lineItems: {
        title__freeTrialEndsAt: '무료 체험 종료일',
        title__paymentMethod: '결제 수단',
        title__statementId: '명세서 ID',
        title__subscriptionBegins: '구독 시작',
        title__totalPaid: '총 결제',
      },
      pastDueNotice: '이전 구독이 연체되어 결제가 되지 않았어요.',
      perMonth: '월',
      title: '결제',
      title__paymentSuccessful: '결제가 완료됐어요!',
      title__subscriptionSuccessful: '성공!',
      title__trialSuccess: '무료 체험이 시작됐어요!',
      totalDueAfterTrial: '{{days}}일 후 무료 체험이 끝나면 결제할 금액',
    },
    credit: '크레딧',
    creditRemainder: '현재 구독 남은 기간에 대한 크레딧',
    defaultFreePlanActive: '현재 무료 플랜을 사용 중이에요',
    free: '무료',
    getStarted: '시작하기',
    keepFreeTrial: '무료 체험 유지',
    keepSubscription: '구독 유지',
    manage: '관리',
    manageSubscription: '구독 관리',
    month: '월',
    monthly: '월간',
    pastDue: '연체',
    pay: '{{amount}} 결제',
    paymentMethod: {
      applePayDescription: {
        annual: '연간 결제',
        monthly: '월간 결제',
      },
      dev: {
        anyNumbers: '아무 숫자나',
        cardNumber: '카드 번호',
        cvcZip: 'CVC, 우편번호',
        developmentMode: '개발 모드',
        expirationDate: '만료일',
        testCardInfo: '테스트 카드 정보',
      },
    },
    paymentMethods__label: '결제 수단',
    highlightedPlanBadge: '인기',
    pricingTable: {
      billingCycle: '결제 주기',
      included: '포함',
    },
    reSubscribe: '다시 구독',
    seeAllFeatures: '전체 기능 보기',
    startFreeTrial: '무료 체험 시작',
    startFreeTrial__days: '{{days}}일 무료 체험 시작',
    subscribe: '구독하기',
    subscriptionDetails: {
      beginsOn: '시작일',
      currentBillingCycle: '현재 결제 주기',
      endsOn: '종료일',
      firstPaymentAmount: '첫 결제 금액',
      firstPaymentOn: '첫 결제일',
      nextPaymentAmount: '다음 결제 금액',
      nextPaymentOn: '다음 결제일',
      pastDueAt: '연체일',
      renewsAt: '갱신일',
      subscribedOn: '구독 시작일',
      title: '구독',
      trialEndsOn: '체험 종료일',
      trialStartedOn: '체험 시작일',
    },
    subtotal: '소계',
    switchPlan: '이 플랜으로 전환',
    switchToAnnual: '연간 결제로 변경',
    switchToAnnualWithAnnualPrice: '연간 {{currency}}{{price}} / 년으로 변경',
    switchToMonthly: '월간 결제로 변경',
    switchToMonthlyWithPrice: '월간 {{currency}}{{price}} / 월로 변경',
    totalDue: '총 결제 금액',
    totalDueToday: '오늘 결제 금액',
    viewFeatures: '기능 보기',
    viewPayment: '결제 보기',
    year: '년',
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle: '권한을 업그레이드하려면 조직 관리자에게 문의하세요.',
      title: '싱글 사인온(SSO)을 관리할 권한이 없습니다',
    },
    navbar: {
      title: '싱글 사인온(SSO) 구성',
    },
    selectProviderStep: {
      title: '공급자 선택',
      subtitle: 'SSO를 설정할 공급자를 선택하세요.',
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: '사용자 지정 SAML 공급자',
      },
      warning: '공급자를 선택하면 구성이 완료될 때까지 다시 변경할 수 없습니다',
    },
    verifyEmailDomainStep: {
      title: '이메일 주소 확인',
      subtitle: '엔터프라이즈 연결을 활성화하려는 이메일 주소를 확인하세요.',
      addEmailAddress: {
        formTitle: '이메일이 필요합니다',
        formSubtitle: '시작하려면 이메일 주소가 필요합니다',
        inputPlaceholder: 'name@company.com',
        inputLabel: '이메일 주소',
      },
      emailCode: {
        formTitle: '이메일 주소를 확인하세요',
        formSubtitle: '{{identifier}}(으)로 전송된 인증 코드를 입력하세요',
        resendButton: '코드를 받지 못하셨나요? 다시 보내기',
        verified: {
          title: '이메일을 받았습니다',
          subtitle: '다음 이메일로 이메일 주소를 확인했습니다',
          inputLabel: '확인된 이메일 주소',
        },
      },
      domainTaken: {
        title: '이 도메인 ({{domain}}) 에는 이미 SSO 연결이 있습니다',
        subtitle: '기존 연결을 통해 접근 권한을 받으려면 애플리케이션 관리자에게 문의하세요.',
      },
    },
  },
  createOrganization: {
    formButtonSubmit: '조직 만들기',
    invitePage: {
      formButtonReset: '넘기기',
    },
    title: '조직 만들기',
  },
  dates: {
    lastDay: "어제 {{ date | timeString('ko-KR') }}",
    next6Days: "다음 {{ date | weekday('ko-KR','long') }} {{ date | timeString('ko-KR') }}",
    nextDay: "내일 {{ date | timeString('ko-KR') }}",
    numeric: "{{ date | numeric('ko-KR') }}",
    previous6Days: "지난 {{ date | weekday('ko-KR','long') }} {{ date | timeString('ko-KR') }}",
    sameDay: "오늘 {{ date | timeString('ko-KR') }}",
  },
  dividerText: '또는',
  footerActionLink__alternativePhoneCodeProvider: '대신 SMS로 코드 받기',
  footerActionLink__useAnotherMethod: '다른 방법 사용하기',
  footerPageLink__help: '도움',
  footerPageLink__privacy: '개인정보처리방침',
  footerPageLink__terms: '약관',
  formButtonPrimary: '계속',
  formButtonPrimary__verify: '확인하기',
  formFieldAction__forgotPassword: '비밀번호를 잊으셨나요?',
  formFieldError__matchingPasswords: '비밀번호가 일치해요.',
  formFieldError__notMatchingPasswords: '비밀번호가 일치하지 않아요.',
  formFieldError__verificationLinkExpired: '인증 링크가 만료됐어요. 새 링크를 요청해 주세요.',
  formFieldHintText__optional: '선택사항',
  formFieldHintText__slug: '슬러그는 사람이 읽기 쉬운 고유 ID예요. 보통 URL에 사용돼요.',
  formFieldInputPlaceholder__apiKeyDescription: '이 키를 만드는 이유를 적어주세요',
  formFieldInputPlaceholder__apiKeyExpirationDate: '날짜를 선택하세요',
  formFieldInputPlaceholder__apiKeyName: '시크릿 키 이름을 입력하세요',
  formFieldInputPlaceholder__backupCode: '백업 코드를 입력하세요',
  formFieldInputPlaceholder__confirmDeletionUserAccount: '계정 삭제',
  formFieldInputPlaceholder__emailAddress: '이메일 주소를 입력하세요',
  formFieldInputPlaceholder__emailAddress_username: '이메일 주소 또는 아이디를 입력하세요',
  formFieldInputPlaceholder__emailAddresses: '이메일 주소를 공백이나 쉼표로 구분해 입력하거나 붙여넣어 주세요',
  formFieldInputPlaceholder__firstName: '이름',
  formFieldInputPlaceholder__lastName: '성',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: '조직 이름',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: '비밀번호를 입력하세요',
  formFieldInputPlaceholder__phoneNumber: '휴대폰 번호를 입력하세요',
  formFieldInputPlaceholder__username: undefined,
  formFieldInput__emailAddress_format: '예시: name@example.com',
  formFieldLabel__apiKey: 'API 키',
  formFieldLabel__apiKeyDescription: '설명',
  formFieldLabel__apiKeyExpiration: '만료',
  formFieldLabel__apiKeyName: '시크릿 키 이름',
  formFieldLabel__automaticInvitations: '이 도메인에 자동 초대 사용',
  formFieldLabel__backupCode: '백업 코드',
  formFieldLabel__confirmDeletion: '확인',
  formFieldLabel__confirmPassword: '비밀번호 확인',
  formFieldLabel__currentPassword: '현재 비밀번호',
  formFieldLabel__emailAddress: '이메일 주소',
  formFieldLabel__emailAddress_username: '이메일 주소 혹은 아이디',
  formFieldLabel__emailAddresses: '이메일 주소',
  formFieldLabel__firstName: '이름',
  formFieldLabel__lastName: '성',
  formFieldLabel__newPassword: '새 비밀번호',
  formFieldLabel__organizationDomain: '도메인',
  formFieldLabel__organizationDomainDeletePending: '대기 중인 초대와 제안 삭제',
  formFieldLabel__organizationDomainEmailAddress: '인증 이메일 주소',
  formFieldLabel__organizationDomainEmailAddressDescription:
    '이 도메인의 이메일 주소를 입력하면 코드를 받아 도메인을 인증할 수 있어요.',
  formFieldLabel__organizationName: '이름',
  formFieldLabel__organizationSlug: '슬러그',
  formFieldLabel__passkeyName: '패스키 이름',
  formFieldLabel__password: '비밀번호',
  formFieldLabel__phoneNumber: '휴대폰 번호',
  formFieldLabel__role: '역할',
  formFieldLabel__signOutOfOtherSessions: '다른 모든 기기에서 로그아웃',
  formFieldLabel__username: '아이디',
  impersonationFab: {
    action__signOut: '로그아웃',
    title: '{{identifier}}로 로그인했어요',
  },
  lastAuthenticationStrategy: '최근 사용',
  maintenanceMode: '지금 점검 중이에요. 걱정 마세요, 몇 분이면 끝날 거예요.',
  membershipRole__admin: '관리자',
  membershipRole__basicMember: '멤버',
  membershipRole__guestMember: '게스트',
  organizationList: {
    action__createOrganization: '조직 만들기',
    action__invitationAccept: '참여',
    action__suggestionsAccept: '참여 요청',
    createOrganization: '조직 만들기',
    invitationAcceptedLabel: '참여함',
    subtitle: '{{applicationName}}로 계속하려면',
    suggestionsAcceptedLabel: '승인 대기',
    title: '계정 선택',
    titleWithoutPersonal: '조직 선택',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API 키',
    },
    badge__automaticInvitation: '자동 초대',
    badge__automaticSuggestion: '자동 제안',
    badge__manualInvitation: '자동 등록 없음',
    badge__unverified: '미인증',
    billingPage: {
      paymentHistorySection: {
        empty: '결제 내역이 없어요',
        notFound: '결제 시도를 찾을 수 없어요',
        tableHeader__amount: '금액',
        tableHeader__date: '날짜',
        tableHeader__status: '상태',
      },
      paymentMethodsSection: {
        actionLabel__default: '기본으로 설정',
        actionLabel__remove: '삭제',
        add: '새 결제 수단 추가',
        addSubtitle: '계정에 새 결제 수단을 추가하세요.',
        cancelButton: '취소',
        formButtonPrimary__add: '결제 수단 추가',
        formButtonPrimary__pay: '{{amount}} 결제',
        payWithTestCardButton: '테스트 카드로 결제',
        removeMethod: {
          messageLine1: '{{identifier}}가 이 계정에서 삭제돼요.',
          messageLine2: '이 결제 수단은 더 이상 사용할 수 없고, 연결된 구독도 작동하지 않아요.',
          successMessage: '{{paymentMethod}}이(가) 계정에서 삭제됐어요.',
          title: '결제 수단 삭제',
        },
        title: '결제 수단',
      },
      start: {
        headerTitle__payments: '결제',
        headerTitle__plans: '플랜',
        headerTitle__statements: '명세서',
        headerTitle__subscriptions: '구독',
      },
      statementsSection: {
        empty: '표시할 명세서가 없어요',
        itemCaption__paidForPlan: '{{plan}} {{period}} 플랜 결제',
        itemCaption__proratedCredit: '이전 구독 일부 사용에 대한 비례 크레딧',
        itemCaption__subscribedAndPaidForPlan: '{{plan}} {{period}} 플랜을 구독하고 결제했어요',
        notFound: '명세서를 찾을 수 없어요',
        tableHeader__amount: '금액',
        tableHeader__date: '날짜',
        title: '명세서',
        totalPaid: '총 결제',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: '관리',
        actionLabel__newSubscription: '플랜 구독하기',
        actionLabel__switchPlan: '플랜 변경',
        tableHeader__edit: '편집',
        tableHeader__plan: '플랜',
        tableHeader__startDate: '시작일',
        title: '구독',
      },
      subscriptionsSection: {
        actionLabel__default: '관리',
      },
      switchPlansSection: {
        title: '플랜 변경',
      },
      title: '결제',
    },
    createDomainPage: {
      subtitle:
        '도메인을 추가해 인증하세요. 이 도메인의 이메일을 가진 사용자는 조직에 자동으로 참여하거나 참여 요청을 할 수 있어요.',
      title: '도메인 추가',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        '초대를 보낼 수 없어요. 다음 이메일 주소에는 이미 대기 중인 초대가 있어요: {{email_addresses}}.',
      formButtonPrimary__continue: '초대 보내기',
      selectDropdown__role: '역할 선택',
      subtitle: '공백이나 쉼표로 구분해 이메일 주소를 입력하거나 붙여넣어 주세요.',
      successMessage: '초대가 성공적으로 전송됐어요',
      title: '새 멤버 초대',
    },
    membersPage: {
      action__invite: '초대',
      action__search: '검색',
      activeMembersTab: {
        menuAction__remove: '회원 제거',
        tableHeader__actions: '작업',
        tableHeader__joined: '가입됨',
        tableHeader__role: '역할',
        tableHeader__user: '사용자',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: '사용 가능한 역할을 업데이트하고 있어요. 끝나면 다시 역할을 바꿀 수 있어요.',
          title: '역할이 잠시 잠겼어요',
        },
      },
      detailsTitle__emptyRow: '표시할 멤버 없음',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            '이메일 도메인을 조직과 연결해 사용자를 초대하세요. 해당 도메인으로 가입한 사용자는 언제든 조직에 참여할 수 있어요.',
          headerTitle: '자동 초대',
          primaryButton: '인증된 도메인 관리',
        },
        table__emptyRow: '표시할 초대가 없어요',
      },
      invitedMembersTab: {
        menuAction__revoke: '초대 취소',
        tableHeader__invited: '초대됨',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle: '해당 도메인으로 가입한 사용자는 조직 참여 요청을 제안받을 수 있어요.',
          headerTitle: '자동 제안',
          primaryButton: '인증된 도메인 관리',
        },
        menuAction__approve: '승인',
        menuAction__reject: '거절',
        tableHeader__requested: '요청됨',
        table__emptyRow: '요청이 없어요',
      },
      start: {
        headerTitle__invitations: '초대',
        headerTitle__members: '멤버',
        headerTitle__requests: '요청',
      },
    },
    navbar: {
      apiKeys: 'API 키',
      billing: '결제',
      description: '조직을 관리하세요.',
      general: '일반',
      members: '멤버',
      title: '조직',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: '이 조직의 결제를 관리할 권한이 없어요.',
      },
      title: '플랜',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: '계속하려면 아래에 {{organizationName}}을(를) 입력하세요.',
          messageLine1: '이 조직을 삭제할까요?',
          messageLine2: '이 작업은 되돌릴 수 없어요.',
          successMessage: '조직을 삭제했어요.',
          title: '조직 삭제',
        },
        leaveOrganization: {
          actionDescription: '계속하려면 아래에 "{{organizationName}}"을(를) 입력하세요.',
          messageLine1: '이 조직을 탈퇴할까요? 조직과 관련 앱에 대한 접근 권한을 잃게 돼요.',
          messageLine2: '이 작업은 되돌릴 수 없어요.',
          successMessage: '조직을 탈퇴했어요.',
          title: '조직 떠나기',
        },
        title: '위험',
      },
      domainSection: {
        menuAction__manage: '관리',
        menuAction__remove: '삭제',
        menuAction__verify: '인증',
        primaryButton: '도메인 추가',
        subtitle: '인증된 이메일 도메인을 기준으로 사용자가 자동 참여하거나 참여 요청을 할 수 있게 해요.',
        title: '인증된 도메인',
      },
      successMessage: '조직이 업데이트됐어요.',
      title: '프로필 업데이트',
    },
    removeDomainPage: {
      messageLine1: '이메일 도메인 {{domain}}이(가) 삭제돼요.',
      messageLine2: '이후에는 사용자가 조직에 자동으로 참여할 수 없어요.',
      successMessage: '{{domain}}이(가) 삭제됐어요.',
      title: '도메인 삭제',
    },
    start: {
      headerTitle__general: '일반',
      headerTitle__members: '멤버',
      profileSection: {
        primaryButton: '프로필 업데이트',
        title: '조직 프로필',
        uploadAction__title: '로고',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: '이 도메인을 삭제하면 초대된 사용자에게 영향이 있어요.',
        removeDomainActionLabel__remove: '도메인 삭제',
        removeDomainSubtitle: '이 도메인을 인증된 도메인 목록에서 삭제',
        removeDomainTitle: '도메인 삭제',
      },
      enrollmentTab: {
        automaticInvitationOption__description: '사용자가 가입하면 자동으로 초대되고 언제든지 조직에 참여할 수 있어요.',
        automaticInvitationOption__label: '자동 초대',
        automaticSuggestionOption__description:
          '사용자는 참여 요청 제안을 받지만, 관리자가 승인해야 조직에 참여할 수 있어요.',
        automaticSuggestionOption__label: '자동 제안',
        calloutInfoLabel: '등록 방식 변경은 새 사용자에게만 적용돼요.',
        calloutInvitationCountLabel: '대기 중인 초대: {{count}}',
        calloutSuggestionCountLabel: '대기 중인 제안: {{count}}',
        manualInvitationOption__description: '사용자는 수동으로만 조직에 초대할 수 있어요.',
        manualInvitationOption__label: '자동 등록 없음',
        subtitle: '이 도메인 사용자가 조직에 참여하는 방법을 선택하세요.',
      },
      start: {
        headerTitle__danger: '위험',
        headerTitle__enrollment: '등록 옵션',
      },
      subtitle: '도메인 {{domain}}이(가) 인증됐어요. 등록 방식을 선택해 주세요.',
      title: '{{domain}} 업데이트',
    },
    verifyDomainPage: {
      formSubtitle: '이메일로 전송된 인증 코드를 입력하세요',
      formTitle: '인증 코드',
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '도메인 {{domainName}}은 이메일로 인증이 필요해요.',
      subtitleVerificationCodeScreen: '{{emailAddress}}로 인증 코드가 전송됐어요. 코드를 입력해 주세요.',
      title: '도메인 인증',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: '조직 전환기 닫기',
    action__createOrganization: '조직 만들기',
    action__invitationAccept: '참여',
    action__manageOrganization: '조직 관리',
    action__openOrganizationSwitcher: '조직 전환기 열기',
    action__suggestionsAccept: '참여 요청',
    notSelected: '선택한 조직 없음',
    personalWorkspace: '개인 워크스페이스',
    suggestionsAcceptedLabel: '승인 대기',
  },
  paginationButton__next: '다음',
  paginationButton__previous: '이전',
  paginationRowText__displaying: '표시중',
  paginationRowText__of: '의',
  reverification: {
    alternativeMethods: {
      actionLink: '도움 요청하기',
      actionText: '이 방법이 없나요?',
      blockButton__backupCode: '백업 코드 사용',
      blockButton__emailCode: '{{identifier}}로 이메일 코드 보내기',
      blockButton__passkey: '패스키 사용하기',
      blockButton__password: '비밀번호로 계속',
      blockButton__phoneCode: '{{identifier}}로 SMS 코드 보내기',
      blockButton__totp: '인증 앱 사용하기',
      getHelp: {
        blockButton__emailSupport: '이메일 지원',
        content: '계정 인증에 문제가 있다면 이메일로 문의해 주세요. 최대한 빠르게 도와드릴게요.',
        title: '도움 요청하기',
      },
      subtitle: '문제가 있나요? 다른 방법으로 인증할 수 있어요.',
      title: '다른 방법 사용하기',
    },
    backupCodeMfa: {
      subtitle: '2단계 인증 설정 때 받은 백업 코드를 입력하세요',
      title: '백업 코드 입력',
    },
    emailCode: {
      formTitle: '인증 코드',
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '이메일로 전송된 코드를 입력해 주세요',
      title: '인증이 필요해요',
    },
    noAvailableMethods: {
      message: '인증을 진행할 수 없어요. 설정된 인증 수단이 없어요.',
      subtitle: '오류가 발생했어요',
      title: '계정을 확인할 수 없어요',
    },
    passkey: {
      blockButton__passkey: '패스키 사용하기',
      subtitle: '패스키로 신원을 확인해요. 기기에서 지문, 얼굴 또는 화면 잠금을 요청할 수 있어요.',
      title: '패스키 사용',
    },
    password: {
      actionLink: '다른 방법 사용하기',
      subtitle: '계속하려면 현재 비밀번호를 입력하세요',
      title: '인증이 필요해요',
    },
    phoneCode: {
      formTitle: '인증 코드',
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '휴대폰으로 전송된 코드를 입력해 주세요',
      title: '인증이 필요해요',
    },
    phoneCodeMfa: {
      formTitle: '인증 코드',
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '휴대폰으로 전송된 코드를 입력해 주세요',
      title: '인증이 필요해요',
    },
    totpMfa: {
      formTitle: '인증 코드',
      subtitle: '인증 앱에서 생성된 코드를 입력해 주세요',
      title: '인증이 필요해요',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: '계정 추가',
      action__signOutAll: '모든 계정 로그아웃',
      subtitle: '계속할 계정을 선택해 주세요.',
      title: '계정 선택',
    },
    alternativeMethods: {
      actionLink: '도움 요청하기',
      actionText: '이 방법이 없나요?',
      blockButton__backupCode: '백업 코드 사용하기',
      blockButton__emailCode: '{{identifier}}로 이메일 코드 보내기',
      blockButton__emailLink: '{{identifier}}로 이메일 링크 보내기',
      blockButton__passkey: '패스키로 로그인',
      blockButton__password: '비밀번호로 로그인',
      blockButton__phoneCode: '{{identifier}}로 SMS 코드 보내기',
      blockButton__totp: '인증 앱 사용하기',
      getHelp: {
        blockButton__emailSupport: '이메일 지원',
        content: '계정 로그인에 문제가 있다면 이메일로 알려 주세요. 최대한 빠르게 접근을 복구해 드릴게요.',
        title: '도움 요청하기',
      },
      subtitle: '문제가 있나요? 다른 방법으로 로그인할 수 있어요.',
      title: '다른 방법 사용하기',
    },
    alternativePhoneCodeProvider: {
      formTitle: '인증 코드',
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '{{provider}} 확인',
    },
    backupCodeMfa: {
      subtitle: '백업 코드는 2단계 인증을 설정할 때 받은 코드예요',
      title: '백업 코드 입력',
    },
    emailCode: {
      formTitle: '인증 코드',
      resendButton: '코드 재전송',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일을 확인하세요',
    },
    emailCodeMfa: {
      formTitle: '이메일을 확인하세요',
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일을 확인하세요',
    },
    emailLink: {
      clientMismatch: {
        subtitle: '계속하려면 로그인 시작한 기기와 브라우저에서 링크를 열어 주세요.',
        title: '이 기기에서는 인증 링크를 사용할 수 없어요',
      },
      expired: {
        subtitle: '계속하려면 원래 탭으로 돌아가세요.',
        title: '이 인증 링크는 만료됐어요',
      },
      failed: {
        subtitle: '계속하려면 원래 탭으로 돌아가세요.',
        title: '이 인증 링크는 유효하지 않아요',
      },
      formSubtitle: '이메일로 전송된 인증 링크를 사용하세요',
      formTitle: '인증 링크',
      loading: {
        subtitle: '곧 이동돼요',
        title: '로그인 중...',
      },
      resendButton: '링크 재전송',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일을 확인하세요',
      unusedTab: {
        title: '이 탭은 닫아도 돼요',
      },
      verified: {
        subtitle: '곧 이동돼요',
        title: '로그인이 완료됐어요',
      },
      verifiedSwitchTab: {
        subtitle: '계속하려면 원래 탭으로 돌아가세요',
        subtitleNewTab: '계속하려면 새로 연 탭으로 돌아가세요',
        titleNewTab: '다른 탭에서 로그인',
      },
    },
    emailLinkMfa: {
      formSubtitle: '이메일로 전송된 확인 링크를 사용하세요',
      resendButton: '링크를 받지 못하셨나요? 다시 보내기',
      subtitle: '{{applicationName}}(으)로 계속',
      title: '이메일을 확인하세요',
    },
    enterpriseConnections: {
      subtitle: '계속할 기업 계정을 선택해 주세요.',
      title: '기업 계정 선택',
    },
    forgotPassword: {
      formTitle: '비밀번호 재설정 코드',
      resendButton: '코드 재전송',
      subtitle: '비밀번호를 재설정하려면',
      subtitle_email: '먼저 이메일로 전송된 코드를 입력하세요',
      subtitle_phone: '먼저 휴대폰으로 전송된 코드를 입력하세요',
      title: '비밀번호 재설정',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: '비밀번호 재설정',
      label__alternativeMethods: '또는 다른 방법으로 로그인',
      title: '비밀번호를 잊으셨나요?',
    },
    newDeviceVerificationNotice: '새로운 기기에서 로그인 중이에요. 계정 보안을 위해 확인을 요청하고 있어요.',
    noAvailableMethods: {
      message: '로그인을 계속할 수 없어요. 사용할 수 있는 인증 방법이 없어요.',
      subtitle: '오류가 발생했어요',
      title: '로그인할 수 없어요',
    },
    passkey: {
      subtitle: '패스키로 로그인하면 본인 확인이 돼요. 기기에서 지문, 얼굴 또는 화면 잠금을 요청할 수 있어요.',
      title: '패스키 사용',
    },
    password: {
      actionLink: '다른 방법 사용하기',
      subtitle: '계정에 등록된 비밀번호를 입력해 주세요',
      title: '비밀번호를 입력하세요',
    },
    passwordCompromised: {
      title: '비밀번호가 유출됐을 수 있어요',
    },
    passwordPwned: {
      title: '유출된 비밀번호예요',
    },
    passwordUntrusted: {
      title: '신뢰할 수 없는 비밀번호예요',
    },
    phoneCode: {
      formTitle: '인증 코드',
      resendButton: '코드 다시 보내기',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '휴대폰 확인',
    },
    phoneCodeMfa: {
      formTitle: '인증 코드',
      resendButton: '코드 다시 보내기',
      subtitle: '계속하려면 휴대폰으로 전송된 인증 코드를 입력하세요',
      title: '휴대폰 확인',
    },
    resetPassword: {
      formButtonPrimary: '비밀번호 재설정',
      requiredMessage: '보안을 위해 비밀번호를 재설정해야 해요.',
      successMessage: '비밀번호가 변경됐어요. 로그인 중이에요. 잠시만 기다려 주세요.',
      title: '비밀번호 재설정',
    },
    resetPasswordMfa: {
      detailsLabel: '비밀번호를 재설정하기 전에 신원을 확인해야 해요.',
    },
    start: {
      actionLink: '회원가입',
      actionLink__join_waitlist: '대기 목록 참여',
      actionLink__use_email: '이메일 사용하기',
      actionLink__use_email_username: '이메일 또는 아이디 사용하기',
      actionLink__use_passkey: '패스키 사용하기',
      actionLink__use_phone: '휴대폰 번호 사용하기',
      actionLink__use_username: '아이디 사용하기',
      actionText: '계정이 없으신가요?',
      actionText__join_waitlist: '미리 사용해 보고 싶으신가요?',
      alternativePhoneCodeProvider: {
        actionLink: '다른 방법 사용하기',
        label: '{{provider}} 휴대폰 번호',
        subtitle: '{{provider}}로 인증 코드를 받을 휴대폰 번호를 입력하세요.',
        title: '{{provider}}로 {{applicationName}}에 로그인',
      },
      subtitle: '환영해요! 계속하려면 로그인해 주세요',
      subtitleCombined: undefined,
      title: '{{applicationName}}에 로그인',
      titleCombined: '{{applicationName}}로 계속',
    },
    totpMfa: {
      formTitle: '인증 코드',
      subtitle: '계속하려면 인증 앱에서 생성된 인증 코드를 입력하세요',
      title: '2단계 인증',
    },
    web3Solana: {
      subtitle: '로그인하려면 아래에서 지갑을 선택하세요',
      title: 'Solana로 로그인',
    },
  },
  signInEnterPasswordTitle: '비밀번호를 입력하세요',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: '코드를 받지 못하셨나요? 다시 보내기',
      subtitle: '{{provider}}로 전송된 인증 코드를 입력하세요',
      title: '{{provider}} 인증',
    },
    continue: {
      actionLink: '로그인',
      actionText: '계정이 있으신가요?',
      subtitle: '계속하려면 누락된 필드에 값을 입력하세요',
      title: '누락된 필드를 입력하세요',
    },
    emailCode: {
      formSubtitle: '이메일 주소로 전송된 인증 코드를 입력하세요',
      formTitle: '인증 코드',
      resendButton: '코드 재전송',
      subtitle: '이메일로 전송된 인증 코드를 입력하세요',
      title: '이메일 인증',
    },
    emailLink: {
      clientMismatch: {
        subtitle: '계속하려면 가입을 시작한 기기와 브라우저에서 링크를 열어 주세요.',
        title: '이 기기에서는 인증 링크를 사용할 수 없어요',
      },
      formSubtitle: '이메일로 받은 인증 링크를 사용해 주세요.',
      formTitle: '인증 링크',
      loading: {
        title: '가입 중...',
      },
      resendButton: '링크 재전송',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일 인증하기',
      verified: {
        title: '가입이 완료됐어요',
      },
      verifiedSwitchTab: {
        subtitle: '계속하려면 새로 연 탭으로 돌아가기',
        subtitleNewTab: '계속하려면 이전 탭으로 돌아가기',
        title: '이메일 인증 성공',
      },
    },
    enterpriseConnections: {
      subtitle: '계속할 기업 계정을 선택해 주세요.',
      title: '기업 계정 선택',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: '{{ privacyPolicyLink || link("개인정보처리방침") }}에 동의해요',
        label__onlyTermsOfService: '{{ termsOfServiceLink || link("서비스 약관") }}에 동의해요',
        label__termsOfServiceAndPrivacyPolicy:
          '{{ termsOfServiceLink || link("서비스 약관") }}과 {{ privacyPolicyLink || link("개인정보처리방침") }}에 동의해요',
      },
      continue: {
        subtitle: '계속하려면 약관에 동의해 주세요',
        title: '약관 동의',
      },
    },
    phoneCode: {
      formSubtitle: '휴대폰 번호로 전송된 인증 코드를 입력하세요.',
      formTitle: '인증 코드',
      resendButton: '코드 재전송',
      subtitle: '휴대폰으로 전송된 인증 코드를 입력하세요',
      title: '휴대폰 번호 인증',
    },
    restrictedAccess: {
      actionLink: '로그인',
      actionText: '이미 계정이 있나요?',
      blockButton__emailSupport: '이메일 지원',
      blockButton__joinWaitlist: '대기 목록 참여',
      subtitle: '현재 회원가입이 중단돼 있어요. 접근 권한이 필요하다면 지원팀에 문의해 주세요.',
      subtitleWaitlist: '회원가입이 중단돼 있어요. 출시 소식을 가장 먼저 받으려면 대기 목록에 참여해 주세요.',
      title: '접근 제한',
    },
    start: {
      actionLink: '로그인하기',
      actionLink__use_email: '이메일로 가입',
      actionLink__use_phone: '휴대폰으로 가입',
      actionText: '계정이 있으신가요?',
      alternativePhoneCodeProvider: {
        actionLink: '다른 방법 사용하기',
        label: '{{provider}} 휴대폰 번호',
        subtitle: '{{provider}}로 인증 코드를 받을 휴대폰 번호를 입력하세요.',
        title: '{{provider}}로 {{applicationName}}에 가입',
      },
      subtitle: '환영해요! 아래 정보를 입력해 주세요.',
      subtitleCombined: '환영해요! 아래 정보를 입력해 주세요.',
      title: '계정 만들기',
      titleCombined: '계정 만들기',
    },
    web3Solana: {
      subtitle: '가입하려면 아래에서 지갑을 선택하세요',
      title: 'Solana로 가입',
    },
  },
  socialButtonsBlockButton: '{{provider|titleize}}로 계속하기',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        '감지된 회사 이름({{organizationName}})과 {{organizationDomain}}에 해당하는 조직이 이미 있어요. 초대로 참여해 주세요.',
    },
    chooseOrganization: {
      action__createOrganization: '새 조직 만들기',
      action__invitationAccept: '참여',
      action__suggestionsAccept: '참여 요청',
      subtitle: '기존 조직에 참여하거나 새 조직을 만드세요',
      subtitle__createOrganizationDisabled: '기존 조직에 참여하세요',
      suggestionsAcceptedLabel: '승인 대기 중',
      title: '조직 선택',
    },
    createOrganization: {
      formButtonReset: '취소',
      formButtonSubmit: '계속',
      formFieldInputPlaceholder__name: '내 조직',
      formFieldInputPlaceholder__slug: 'nae-jigug',
      formFieldLabel__name: '이름',
      formFieldLabel__slug: '슬러그',
      subtitle: '조직 정보를 입력해 계속하세요',
      title: '조직 설정',
    },
    organizationCreationDisabled: {
      subtitle: '초대가 필요하면 조직 관리자에게 문의해 주세요.',
      title: '조직에 소속돼야 해요',
    },
    signOut: {
      actionLink: '로그아웃',
      actionText: '{{identifier}}로 로그인됨',
    },
  },
  taskResetPassword: {
    formButtonPrimary: '비밀번호 재설정',
    signOut: {
      actionLink: '로그아웃',
      actionText: '{{identifier}}로 로그인됨',
    },
    subtitle: '계속하려면 비밀번호를 새로 설정해야 해요',
    title: '비밀번호 재설정',
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}}은(는) 이미 이 조직의 멤버예요.',
    avatar_file_size_exceeded: '파일 크기가 최대 10MB 제한을 초과해요. 더 작은 파일을 선택해 주세요.',
    avatar_file_type_invalid: '지원되지 않는 파일 형식이에요. JPG, PNG, GIF 또는 WEBP 이미지를 업로드해 주세요.',
    captcha_invalid:
      '보안 검증에 실패해 회원가입을 완료할 수 없어요. 페이지를 새로고침한 뒤 다시 시도하거나 지원팀에 문의해 주세요.',
    captcha_unavailable:
      '봇 검증에 실패해 회원가입을 완료할 수 없어요. 페이지를 새로고침한 뒤 다시 시도하거나 지원팀에 문의해 주세요.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      '임시 이메일 서비스는 지원되지 않아요. 계정을 만들려면 일반 이메일 주소를 사용해 주세요.',
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: '이 정보와 일치하는 계정을 찾을 수 없어요.',
    form_new_password_matches_current: '새 비밀번호는 현재 비밀번호와 같을 수 없어요.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: '이메일 주소 형식이 올바르지 않아요.',
    form_param_format_invalid__phone_number: '전화번호는 올바른 국제 형식이어야 해요.',
    form_param_max_length_exceeded__first_name: '이름은 256자를 넘을 수 없어요.',
    form_param_max_length_exceeded__last_name: '성은 256자를 넘을 수 없어요.',
    form_param_max_length_exceeded__name: '이름은 256자를 넘을 수 없어요.',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: '비밀번호가 너무 짧아요. 최소 8자 이상이어야 해요.',
    form_password_not_strong_enough: '비밀번호가 충분히 안전하지 않아요.',
    form_password_or_identifier_incorrect:
      '비밀번호 또는 이메일 주소가 올바르지 않아요. 다시 시도하거나 다른 방법을 사용해 보세요.',
    form_password_pwned: '이 비밀번호는 유출된 기록이 있어 사용할 수 없어요. 다른 비밀번호를 사용해 주세요.',
    form_password_pwned__sign_in: '이 비밀번호는 유출된 비밀번호예요. 비밀번호를 재설정해 주세요.',
    form_password_size_in_bytes_exceeded:
      '비밀번호가 허용되는 최대 바이트 수를 초과했어요. 비밀번호를 줄이거나 일부 특수 문자를 제거해 주세요.',
    form_password_untrusted__sign_in:
      '비밀번호가 유출됐을 수 있어요. 계정을 보호하기 위해 다른 방법으로 로그인해 주세요. 로그인 후 비밀번호를 재설정해야 해요.',
    form_password_validation_failed: '잘못된 비밀번호',
    form_username_invalid_character: undefined,
    form_username_invalid_length: '아이디는 {{min_length}}~{{max_length}}자여야 해요.',
    form_username_needs_non_number_char: '아이디에는 숫자가 아닌 문자가 하나 이상 포함돼야 해요.',
    identification_deletion_failed: '마지막 식별 정보는 삭제할 수 없어요.',
    not_allowed_access:
      "이메일 주소 또는 전화번호는 가입에 사용할 수 없어요. 이메일 주소에 '+', '=', '#' 또는 '.'이 포함됐거나 임시 이메일 서비스 도메인이거나, 명시적으로 제외된 경우예요. 이 오류가 계속되면 지원에 문의해 주세요.",
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: '이 조직의 멤버가 아니에요. 다른 조직을 선택하거나 새로 만들어 주세요.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      '이 조직의 멤버가 아니에요. 다른 조직을 선택해 주세요.',
    passkey_already_exists: '이 기기에 이미 패스키가 등록되어 있어요.',
    passkey_not_supported: '이 기기에서는 패스키를 지원하지 않아요.',
    passkey_pa_not_supported: '등록을 위해 플랫폼 인증기가 필요하지만, 이 기기는 지원하지 않아요.',
    passkey_registration_cancelled: '패스키 등록이 취소되었거나 시간이 초과됐어요.',
    passkey_retrieval_cancelled: '패스키 인증이 취소되었거나 시간이 초과됐어요.',
    passwordComplexity: {
      maximumLength: '{{length}} 보다 짧은 문자열',
      minimumLength: '{{length}} 또는 그 이상의 문자열',
      requireLowercase: '소문자',
      requireNumbers: '숫자',
      requireSpecialCharacter: '특수문자',
      requireUppercase: '대문자',
      sentencePrefix: '비밀번호에는 다음이 꼭 포함돼야 해요',
    },
    phone_number_exists: '이 전화번호는 이미 사용 중이에요. 다른 번호를 시도해 주세요.',
    session_exists: '이미 로그인 중이에요.',
    web3_missing_identifier: 'Web3 지갑 확장 프로그램을 찾을 수 없어요. 계속하려면 설치해 주세요.',
    web3_signature_request_rejected: '서명 요청을 거부했어요. 계속하려면 다시 시도해 주세요.',
    web3_solana_signature_generation_failed: '서명을 생성하는 동안 오류가 발생했어요. 계속하려면 다시 시도해 주세요.',
    zxcvbn: {
      couldBeStronger: '비밀번호는 괜찮지만 더 강하게 만들 수 있어요. 문자를 더 추가해 보세요.',
      goodPassword: '좋아요! 훌륭한 비밀번호예요.',
      notEnough: '비밀번호가 충분히 안전하지 않아요.',
      suggestions: {
        allUppercase: '모든 글자가 아닌 일부 글자만 대문자로 바꿔 보세요.',
        anotherWord: '덜 흔한 단어를 더 추가해 보세요.',
        associatedYears: '나와 관련된 연도는 피하는 게 좋아요.',
        capitalization: '첫 글자 외에도 대문자를 섞어 보세요.',
        dates: '나와 관련된 날짜나 연도는 피하는 게 좋아요.',
        l33t: "'a'에서 '@'와 같이 예측 가능한 문자 대체를 피하세요.",
        longerKeyboardPattern: '더 긴 키보드 패턴을 쓰고 입력 방향을 바꿔 보세요.',
        noNeed: '기호, 숫자, 대문자 없이도 강한 비밀번호를 만들 수 있어요.',
        pwned: '이 비밀번호를 다른 곳에서도 쓴다면 변경해 주세요.',
        recentYears: '최근 연도는 피하세요.',
        repeated: '반복되는 단어나 문자는 피하세요.',
        reverseWords: '익숙한 단어를 거꾸로 쓰지 마세요.',
        sequences: '흔한 문자 순서는 피하세요.',
        useWords: '여러 단어를 쓰되 흔한 문구는 피하세요.',
      },
      warnings: {
        common: '자주 쓰는 비밀번호예요.',
        commonNames: '흔한 이름이나 성은 추측하기 쉬워요.',
        dates: '날짜는 쉽게 추측돼요.',
        extendedRepeat: '"abcabcabc" 같은 반복 패턴은 쉽게 추측돼요.',
        keyPattern: '짧은 키보드 패턴은 추측하기 쉬워요.',
        namesByThemselves: '단일 이름이나 성은 추측하기 쉬워요.',
        pwned: '인터넷 유출로 비밀번호가 노출됐어요.',
        recentYears: '최근 연도는 추측하기 쉬워요.',
        sequences: '"abc" 같은 흔한 문자 순서는 추측하기 쉬워요.',
        similarToCommon: '자주 쓰는 비밀번호와 비슷해요.',
        simpleRepeat: '"aaa" 같은 반복 문자는 추측하기 쉬워요.',
        straightRow: '키보드 한 줄 패턴은 추측하기 쉬워요.',
        topHundred: '자주 쓰는 비밀번호예요.',
        topTen: '아주 많이 쓰는 비밀번호예요.',
        userInputs: '개인 정보나 페이지 관련 데이터는 피하는 게 좋아요.',
        wordByItself: '한 단어만 쓰면 추측하기 쉬워요.',
      },
    },
  },
  userButton: {
    action__addAccount: '계정 추가',
    action__closeUserMenu: '사용자 메뉴 닫기',
    action__manageAccount: '계정 관리',
    action__openUserMenu: '사용자 메뉴 열기',
    action__signOut: '로그아웃',
    action__signOutAll: '모든 계정에서 로그아웃',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API 키',
    },
    backupCodePage: {
      actionLabel__copied: '복사 완료!',
      actionLabel__copy: '전체 복사',
      actionLabel__download: '.txt 다운로드',
      actionLabel__print: '인쇄',
      infoText1: '이 계정에 백업 코드가 활성화돼요.',
      infoText2: '백업 코드는 비밀로 보관해 주세요. 노출이 의심되면 다시 생성할 수 있어요.',
      subtitle__codelist: '안전하게 저장하고 비밀로 유지하세요.',
      successMessage:
        '이제 백업 코드가 활성화됐어요. 인증 장치에 접근할 수 없을 때 이 중 하나로 로그인할 수 있어요. 각 코드는 한 번만 사용할 수 있어요.',
      successSubtitle: '인증 장치에 접근할 수 없을 때 이 중 하나로 로그인할 수 있어요.',
      title: '백업 코드 인증 추가',
      title__codelist: '백업 코드',
    },
    billingPage: {
      paymentHistorySection: {
        empty: '결제 내역이 없어요',
        notFound: '결제 시도를 찾을 수 없어요',
        tableHeader__amount: '금액',
        tableHeader__date: '날짜',
        tableHeader__status: '상태',
      },
      paymentMethodsSection: {
        actionLabel__default: '기본으로 설정',
        actionLabel__remove: '삭제',
        add: '새 결제 수단 추가',
        addSubtitle: '계정에 새 결제 수단을 추가하세요.',
        cancelButton: '취소',
        formButtonPrimary__add: '결제 수단 추가',
        formButtonPrimary__pay: '{{amount}} 결제',
        payWithTestCardButton: '테스트 카드로 결제',
        removeMethod: {
          messageLine1: '{{identifier}}가 이 계정에서 삭제돼요.',
          messageLine2: '이 결제 수단은 더 이상 사용할 수 없고, 연결된 구독도 작동하지 않아요.',
          successMessage: '{{paymentMethod}}이(가) 계정에서 삭제됐어요.',
          title: '결제 수단 삭제',
        },
        title: '결제 수단',
      },
      start: {
        headerTitle__payments: '결제',
        headerTitle__plans: '플랜',
        headerTitle__statements: '명세서',
        headerTitle__subscriptions: '구독',
      },
      statementsSection: {
        empty: '표시할 명세서가 없어요',
        itemCaption__paidForPlan: '{{plan}} {{period}} 플랜 결제',
        itemCaption__proratedCredit: '이전 구독 일부 사용에 대한 비례 크레딧',
        itemCaption__subscribedAndPaidForPlan: '{{plan}} {{period}} 플랜을 구독하고 결제했어요',
        notFound: '명세서를 찾을 수 없어요',
        tableHeader__amount: '금액',
        tableHeader__date: '날짜',
        title: '명세서',
        totalPaid: '총 결제',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: '관리',
        actionLabel__newSubscription: '플랜 구독하기',
        actionLabel__switchPlan: '플랜 변경',
        tableHeader__edit: '편집',
        tableHeader__plan: '플랜',
        tableHeader__startDate: '시작일',
        title: '구독',
      },
      subscriptionsSection: {
        actionLabel__default: '관리',
      },
      switchPlansSection: {
        title: '플랜 변경',
      },
      title: '결제',
    },
    connectedAccountPage: {
      formHint: '계정을 연결할 제공자를 선택하세요',
      formHint__noAccounts: '사용 가능한 외부 계정 제공자가 없어요.',
      removeResource: {
        messageLine1: '{{identifier}}가 이 계정에서 제거될 예정이에요.',
        messageLine2: '이 연결된 계정은 더 이상 사용할 수 없고, 관련 기능도 작동하지 않아요.',
        successMessage: '{{connectedAccount}}가 계정에서 삭제됐어요.',
        title: '연결된 계정 제거',
      },
      socialButtonsBlockButton: '{{provider|titleize}} 계정 연결',
      successMessage: '이 제공자가 계정에 추가됐어요.',
      title: '연결된 계정 추가하기',
    },
    deletePage: {
      actionDescription: '계속하려면 아래에 계정 삭제를 입력하세요.',
      confirm: '계정 삭제',
      messageLine1:
        '계정을 삭제할까요? 일부 관련 데이터는 보관될 수 있어요. 전체 데이터 삭제를 요청하려면 고객 지원에 문의해 주세요.',
      messageLine2: '이 작업은 되돌릴 수 없어요.',
      title: '계정 삭제',
    },
    emailAddressPage: {
      emailCode: {
        formHint: '이 이메일 주소로 인증 코드가 포함된 이메일이 전송돼요.',
        formSubtitle: '{{identifier}}에게 전송된 인증 코드를 입력해 주세요',
        formTitle: '인증 코드',
        resendButton: '코드 재전송',
        successMessage: '{{identifier}} 이메일이 계정에 추가됐어요.',
      },
      emailLink: {
        formHint: '인증 링크가 포함된 이메일이 이 이메일 주소로 전송돼요.',
        formSubtitle: '{{identifier}}에게 전송된 이메일의 링크를 클릭해 주세요.',
        formTitle: '인증 링크',
        resendButton: '링크 재전송',
        successMessage: '{{identifier}} 이메일이 계정에 추가됐어요.',
      },
      enterpriseSSOLink: {
        formButton: '로그인 계속하기',
        formSubtitle: '{{identifier}}로 로그인 완료',
      },
      formHint: '이 이메일 주소를 추가하려면 먼저 인증해야 해요.',
      removeResource: {
        messageLine1: '{{identifier}} 이메일이 이 계정에서 제거될 예정이에요.',
        messageLine2: '더 이상 이 이메일 주소로 로그인할 수 없어요.',
        successMessage: '{{emailAddress}} 이메일이 계정에서 삭제됐어요.',
        title: '이메일 제거',
      },
      title: '이메일 주소 추가',
      verifyTitle: '이메일 주소 인증',
    },
    formButtonPrimary__add: '추가',
    formButtonPrimary__continue: '계속',
    formButtonPrimary__finish: '완료',
    formButtonPrimary__remove: '삭제',
    formButtonPrimary__save: '저장',
    formButtonReset: '취소',
    mfaPage: {
      formHint: '추가할 방법을 선택해 주세요.',
      title: '2단계 인증 추가',
    },
    mfaPhoneCodePage: {
      backButton: '기존 번호 사용',
      primaryButton__addPhoneNumber: '휴대폰 번호 추가',
      removeResource: {
        messageLine1: '{{identifier}}는 로그인할 때 더 이상 인증 코드를 받지 않아요.',
        messageLine2: '계정이 안전하지 않을 수 있어요. 계속할까요?',
        successMessage: '{{mfaPhoneCode}}에 대한 SMS 코드 2단계 인증이 제거됐어요.',
        title: '2단계 인증 제거',
      },
      subtitle__availablePhoneNumbers: 'SMS 코드 2단계 인증을 위해 등록할 휴대폰 번호를 선택하세요.',
      subtitle__unavailablePhoneNumbers: 'SMS 코드 2단계 인증에 등록할 수 있는 휴대폰 번호가 없어요.',
      successMessage1: '로그인할 때 이 휴대폰 번호로 전송된 인증 코드를 추가로 입력해야 해요.',
      successMessage2:
        '백업 코드를 안전한 곳에 보관해 주세요. 인증 기기에 접근할 수 없게 되면 백업 코드로 로그인할 수 있어요.',
      successTitle: 'SMS 코드 인증이 활성화됐어요',
      title: 'SMS 코드 인증 추가',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: '대신 QR 코드 스캔 하세요',
        buttonUnableToScan__nonPrimary: 'QR 코드를 스캔할 수 없나요?',
        infoText__ableToScan: '인증 앱에서 새 로그인 방법을 만들고 QR 코드를 스캔해 계정에 연결해 주세요.',
        infoText__unableToScan: '인증 장치에서 새 로그인 방법을 만든 다음 아래 키를 입력해 주세요.',
        inputLabel__unableToScan1: '시간 기반 또는 일회용 비밀번호가 켜져 있는지 확인한 뒤 계정 연결을 완료해 주세요.',
        inputLabel__unableToScan2: '인증 앱이 TOTP URI를 지원한다면 전체 URI를 복사해도 돼요.',
      },
      removeResource: {
        messageLine1: '로그인할 때 더 이상 이 인증자의 인증 코드가 필요하지 않아요.',
        messageLine2: '계정이 안전하지 않을 수 있어요. 계속할까요?',
        successMessage: '인증 앱을 통한 2단계 인증이 제거됐어요.',
        title: '2단계 인증 제거',
      },
      successMessage: '이제 2단계 인증이 활성화됐어요. 로그인할 때 추가 단계로 이 인증자의 인증 코드를 입력해야 해요.',
      title: '인증 애플리케이션 추가',
      verifySubtitle: '인증 앱이 생성한 인증 코드를 입력해 주세요',
      verifyTitle: '인증 코드',
    },
    mobileButton__menu: '메뉴',
    navbar: {
      account: '프로필',
      apiKeys: 'API 키',
      billing: '결제',
      description: '계정 정보를 관리하세요.',
      security: '보안',
      title: '계정',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}}이(가) 이 계정에서 삭제돼요.',
        title: '패스키 삭제',
      },
      subtitle__rename: '패스키 이름을 바꾸면 더 쉽게 찾을 수 있어요.',
      title__rename: '패스키 이름 변경',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        '이전 비밀번호를 사용했을 수 있는 다른 기기에서 로그아웃하는 것을 권장해요.',
      readonly: '기업 연결로만 로그인할 수 있어 비밀번호를 변경할 수 없어요.',
      successMessage__set: '비밀번호가 설정됐어요.',
      successMessage__signOutOfOtherSessions: '다른 모든 기기는 로그아웃됐어요.',
      successMessage__update: '비밀번호가 업데이트됐어요.',
      title__set: '비밀번호 설정',
      title__update: '비밀번호 변경',
    },
    phoneNumberPage: {
      infoText: '인증 코드가 포함된 문자 메시지가 이 휴대폰 번호로 전송돼요. 통신사 요금이 부과될 수 있어요.',
      removeResource: {
        messageLine1: '{{identifier}}가 계정에서 제거될 예정이에요.',
        messageLine2: '더 이상 이 휴대폰 번호로 로그인할 수 없어요.',
        successMessage: '{{phoneNumber}}가 계정에서 제거됐어요.',
        title: '휴대폰 번호 제거',
      },
      successMessage: '{{identifier}}가 계정에 추가됐어요.',
      title: '휴대폰 번호 추가',
      verifySubtitle: '{{identifier}}로 전송된 인증 코드를 입력하세요',
      verifyTitle: '휴대폰 번호 인증',
    },
    plansPage: {
      title: '플랜',
    },
    profilePage: {
      fileDropAreaHint: '10MB보다 작은 JPG, PNG, GIF, WEBP 이미지를 업로드해 주세요.',
      imageFormDestructiveActionSubtitle: '이미지 제거',
      imageFormSubtitle: '이미지 업로드',
      imageFormTitle: '프로필 이미지',
      readonly: '기업 연결에서 제공된 프로필 정보라 수정할 수 없어요.',
      successMessage: '프로필이 업데이트됐어요.',
      title: '프로필 업데이트',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: '이 장치에서 로그아웃',
        title: '활성화된 장치',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: '재시도',
        actionLabel__reauthorize: '지금 인증하기',
        destructiveActionTitle: '제거',
        primaryButton: '계정 연결하기',
        subtitle__disconnected: '이 계정은 연결이 해제됐어요.',
        subtitle__reauthorize:
          '필요한 권한 범위가 업데이트되어 일부 기능이 제한될 수 있어요. 문제를 피하려면 이 애플리케이션을 다시 인증해 주세요.',
        title: '연결된 계정',
      },
      dangerSection: {
        deleteAccountButton: '계정 삭제',
        title: '위험',
      },
      emailAddressesSection: {
        destructiveAction: '이메일 주소 제거',
        detailsAction__nonPrimary: '기본으로 설정',
        detailsAction__primary: '인증 완료',
        detailsAction__unverified: '인증 완료',
        primaryButton: '이메일 주소 추가',
        title: '이메일 주소',
      },
      enterpriseAccountsSection: {
        primaryButton: '계정 연결하기',
        title: '기업 계정',
      },
      headerTitle__account: '프로필',
      headerTitle__security: '보안',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: '코드 재생성',
          headerTitle: '백업 코드',
          subtitle__regenerate: '새로운 보안 백업 코드 세트를 받아 보세요. 이전 백업 코드는 삭제돼 사용할 수 없어요.',
          title__regenerate: '백업 코드 재생성',
        },
        phoneCode: {
          actionLabel__setDefault: '기본으로 설정',
          destructiveActionLabel: '휴대폰 번호 제거',
        },
        primaryButton: '2단계 인증 추가',
        title: '2단계 인증',
        totp: {
          destructiveActionTitle: '제거',
          headerTitle: '인증 애플리케이션',
        },
      },
      passkeysSection: {
        menuAction__destructive: '삭제',
        menuAction__rename: '이름 변경',
        primaryButton: '패스키 추가',
        title: '패스키',
      },
      passwordSection: {
        primaryButton__setPassword: '비밀번호 설정',
        primaryButton__updatePassword: '비밀번호 변경',
        title: '비밀번호',
      },
      phoneNumbersSection: {
        destructiveAction: '휴대폰 번호 제거',
        detailsAction__nonPrimary: '기본으로 설정',
        detailsAction__primary: '인증 완료',
        detailsAction__unverified: '인증 완료',
        primaryButton: '휴대폰 번호 추가',
        title: '휴대폰 번호',
      },
      profileSection: {
        primaryButton: '프로필 수정',
        title: '프로필',
      },
      usernameSection: {
        primaryButton__setUsername: '아이디 설정',
        primaryButton__updateUsername: '아이디 변경',
        title: '아이디',
      },
      web3WalletsSection: {
        destructiveAction: '지갑 제거',
        detailsAction__nonPrimary: '기본으로 설정',
        primaryButton: '지갑 연결하기',
        title: 'Web3 지갑',
        web3SelectSolanaWalletScreen: {
          subtitle: '계정에 연결할 Solana 지갑을 선택하세요.',
          title: 'Solana 지갑 추가',
        },
      },
    },
    usernamePage: {
      successMessage: '아이디가 업데이트됐어요.',
      title__set: '아이디 업데이트',
      title__update: '아이디 업데이트',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}}가 이 계정에서 제거될 예정이에요.',
        messageLine2: '더 이상 이 Web3 지갑으로 로그인할 수 없어요.',
        successMessage: '{{web3Wallet}}가 계정에서 제거됐어요.',
        title: 'web3 지갑 제거',
      },
      subtitle__availableWallets: '계정에 연결할 web3 지갑을 선택하세요.',
      subtitle__unavailableWallets: '사용 가능한 Web3 지갑이 없어요.',
      successMessage: '지갑이 계정에 추가됐어요.',
      title: 'web3 지갑 추가',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: '로그인',
      actionText: '이미 액세스 권한이 있으신가요?',
      formButton: '대기 목록에 가입',
      subtitle: '이메일 주소를 입력하면 준비되는 대로 알려드릴게요',
      title: '대기 목록에 가입',
    },
    success: {
      message: '곧 이동돼요...',
      subtitle: '준비되는 대로 연락드릴게요',
      title: '대기 목록에 함께해 주셔서 고마워요!',
    },
  },
  web3SolanaWalletButtons: {
    connect: '{{walletName}}(으)로 연결',
    continue: '{{walletName}}(으)로 계속',
    noneAvailable:
      'Solana Web3 지갑을 찾을 수 없어요. Web3를 지원하는 {{ solanaWalletsLink || link("wallet extension") }}을(를) 설치해 주세요.',
  },
} as const;
